<template>
    <div class="analyticsView">
        <div class="analyticsHero">
            <div>
                <p class="eyebrow">Code Analytics</p>
                <h1>{{ heading }}</h1>
                <p v-if="analytics && analytics.generatedAt" class="heroMeta">
                    Generated {{ formatDateTime(analytics.generatedAt) }}. Visits are deduplicated within {{
                        analytics.visitDedupSeconds || 60
                    }} seconds per IP.
                </p>
                <p v-else-if="loadingMessage" class="heroMeta">{{ loadingMessage }}</p>
                <p v-if="errorMessage" class="heroError">{{ errorMessage }}</p>
            </div>
            <div class="heroActions">
                <router-link :to="`/code/${codeid}`" class="btn btn-primary">Back to Code</router-link>
                <a v-if="analyticsHtmlUrl" :href="analyticsHtmlUrl" target="_blank"
                    rel="noopener noreferrer" class="btn btn-primary">Open Server View</a>
            </div>
        </div>

        <form class="filtersPanel" @submit.prevent="applyFilters">
            <div class="filtersGrid">
                <div class="field">
                    <label for="groupBy">Group By</label>
                    <select id="groupBy" class="form-control" v-model="filters.groupBy">
                        <option v-for="option in groupByOptions" :key="option" :value="option">{{ option }}</option>
                    </select>
                </div>
                <div class="field">
                    <label for="range">Range</label>
                    <select id="range" class="form-control" v-model="filters.range">
                        <option v-for="option in rangeOptions" :key="option" :value="option">{{ option }}</option>
                    </select>
                </div>
                <div class="field">
                    <label for="country">Country</label>
                    <select id="country" class="form-control" v-model="filters.country">
                        <option value="all">all</option>
                        <option v-for="country in availableCountries" :key="country" :value="country">{{ country }}</option>
                    </select>
                </div>
                <div class="field">
                    <label for="state">State</label>
                    <select id="state" class="form-control" v-model="filters.state">
                        <option value="all">all</option>
                        <option v-for="state in availableStates" :key="state" :value="state">{{ state }}</option>
                    </select>
                </div>
                <div class="field">
                    <label for="city">City</label>
                    <select id="city" class="form-control" v-model="filters.city">
                        <option value="all">all</option>
                        <option v-for="city in availableCities" :key="city" :value="city">{{ city }}</option>
                    </select>
                </div>
                <div class="field">
                    <label for="from">From</label>
                    <input id="from" class="form-control" type="date" v-model="filters.from" />
                </div>
                <div class="field">
                    <label for="to">To</label>
                    <input id="to" class="form-control" type="date" v-model="filters.to" />
                </div>
            </div>
            <div class="filterActions">
                <button class="btn btn-primary" type="submit">Apply Filters</button>
                <button class="btn btn-primary" type="button" @click="resetFilters">Reset</button>
            </div>
        </form>

        <div v-if="analytics" class="analyticsContent">
            <section class="summaryGrid">
                <div class="summaryCard">
                    <span>Total Visits</span>
                    <strong>{{ analytics.totalVisits ?? 0 }}</strong>
                </div>
                <div class="summaryCard">
                    <span>Countries</span>
                    <strong>{{ analytics.uniqueCountries ?? 0 }}</strong>
                </div>
                <div class="summaryCard">
                    <span>States</span>
                    <strong>{{ analytics.uniqueStates ?? 0 }}</strong>
                </div>
                <div class="summaryCard">
                    <span>Cities</span>
                    <strong>{{ analytics.uniqueCities ?? 0 }}</strong>
                </div>
            </section>

            <section class="panel chartPanel">
                <div class="panelHeading">
                    <div>
                        <h2>Visits Over Time</h2>
                        <p>{{ seriesSummary }}</p>
                    </div>
                </div>
                <div v-if="chartPoints.length > 0" class="chartShell">
                    <svg viewBox="0 0 900 320" preserveAspectRatio="none" role="img" aria-label="Visit chart">
                        <g v-for="gridLine in chartGridLines" :key="gridLine.value">
                            <line :x1="chartPadding.left" :y1="gridLine.y" :x2="900 - chartPadding.right" :y2="gridLine.y"
                                class="chartGridLine" />
                            <text x="8" :y="gridLine.y + 4" class="chartGridLabel">{{ gridLine.value }}</text>
                        </g>
                        <line :x1="chartPadding.left" :y1="chartPadding.top + chartInnerHeight"
                            :x2="900 - chartPadding.right" :y2="chartPadding.top + chartInnerHeight" class="chartAxis" />
                        <path :d="chartPathData" class="chartPath" />
                        <g v-for="(point, index) in chartPoints" :key="point.label + '-' + index">
                            <circle :cx="point.x" :cy="point.y" r="4.5" class="chartDot">
                                <title>{{ point.label }}: {{ point.count }} visits</title>
                            </circle>
                            <text :x="point.x" y="306" class="chartXAxisLabel">{{ point.label }}</text>
                        </g>
                    </svg>
                </div>
                <p v-else class="emptyState">No visits found for the selected filters.</p>
            </section>

            <section class="tableGrid">
                <section class="panel">
                    <h3>Countries</h3>
                    <table v-if="analytics.countries && analytics.countries.length > 0">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Location</th>
                                <th>Visits</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, index) in analytics.countries" :key="row.key || row.label">
                                <td>{{ index + 1 }}</td>
                                <td>{{ row.label }}</td>
                                <td>{{ row.count }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <p v-else class="emptyState">No country data.</p>
                </section>

                <section class="panel">
                    <h3>States</h3>
                    <table v-if="analytics.states && analytics.states.length > 0">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Location</th>
                                <th>Visits</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, index) in analytics.states" :key="row.key || row.label">
                                <td>{{ index + 1 }}</td>
                                <td>{{ row.label }}</td>
                                <td>{{ row.count }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <p v-else class="emptyState">No state data.</p>
                </section>

                <section class="panel">
                    <h3>Cities</h3>
                    <table v-if="analytics.cities && analytics.cities.length > 0">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Location</th>
                                <th>Visits</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, index) in analytics.cities" :key="row.key || row.label">
                                <td>{{ index + 1 }}</td>
                                <td>{{ row.label }}</td>
                                <td>{{ row.count }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <p v-else class="emptyState">No city data.</p>
                </section>

                <section class="panel panelWide">
                    <h3>Map Points</h3>
                    <table v-if="analytics.mapPoints && analytics.mapPoints.length > 0">
                        <thead>
                            <tr>
                                <th>Location</th>
                                <th>Lat</th>
                                <th>Lon</th>
                                <th>Visits</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, index) in analytics.mapPoints" :key="row.label + '-' + index">
                                <td>{{ row.label }}</td>
                                <td>{{ row.lat }}</td>
                                <td>{{ row.lon }}</td>
                                <td>{{ row.count }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <p v-else class="emptyState">No geolocation points available.</p>
                </section>
            </section>
        </div>
    </div>
</template>

<script>
import { getCodeAnalyticsFromApi, getCodeAnalyticsUrl } from "../../libs/libs.js";

const DEFAULT_FILTERS = {
    groupBy: "day",
    range: "week",
    country: "US",
    state: "all",
    city: "all",
    from: "",
    to: "",
};

export default {
    name: "CodeAnalyticsView",
    display: "Code Analytics",
    props: ["codeid"],
    data() {
        return {
            analytics: null,
            filters: { ...DEFAULT_FILTERS },
            groupByOptions: ["day", "week", "month", "year", "all"],
            rangeOptions: ["week", "month", "year", "all"],
            loadingMessage: "Loading analytics...",
            errorMessage: "",
            chartPadding: {
                top: 20,
                right: 20,
                bottom: 46,
                left: 52,
            },
        };
    },
    computed: {
        heading() {
            if (!this.analytics) {
                return `${this.codeid} analytics`;
            }
            return this.analytics.codeTitle
                ? `${this.analytics.codeTitle} analytics`
                : `${this.analytics.shortcode} analytics`;
        },
        analyticsUrl() {
            return getCodeAnalyticsUrl(this.codeid, this.filters);
        },
        analyticsHtmlUrl() {
            const analyticsUrl = new URL(this.analyticsUrl);
            analyticsUrl.searchParams.delete("json");
            return analyticsUrl.toString();
        },
        availableCountries() {
            return this.analytics?.availableCountries || [];
        },
        availableStates() {
            return this.analytics?.availableStates || [];
        },
        availableCities() {
            return this.analytics?.availableCities || [];
        },
        chartInnerWidth() {
            return 900 - this.chartPadding.left - this.chartPadding.right;
        },
        chartInnerHeight() {
            return 320 - this.chartPadding.top - this.chartPadding.bottom;
        },
        chartPoints() {
            const series = this.analytics?.series || [];
            if (series.length <= 0) {
                return [];
            }
            const maxCount = Math.max(1, ...series.map((point) => point.count || 0));
            const xStep = series.length > 1 ? this.chartInnerWidth / (series.length - 1) : this.chartInnerWidth / 2;
            return series.map((point, index) => {
                const x = this.chartPadding.left + (series.length > 1 ? xStep * index : this.chartInnerWidth / 2);
                const y = this.chartPadding.top + this.chartInnerHeight - ((point.count || 0) / maxCount) * this.chartInnerHeight;
                return {
                    x,
                    y,
                    label: point.label,
                    count: point.count || 0,
                };
            });
        },
        chartPathData() {
            return this.chartPoints
                .map((point, index) => `${index === 0 ? "M" : "L"}${point.x} ${point.y}`)
                .join(" ");
        },
        chartGridLines() {
            const series = this.analytics?.series || [];
            const maxCount = Math.max(1, ...series.map((point) => point.count || 0));
            return Array.from({ length: 5 }, (_, index) => {
                const value = Math.round((maxCount / 4) * index);
                const y = this.chartPadding.top + this.chartInnerHeight - (this.chartInnerHeight / 4) * index;
                return { value, y };
            });
        },
        seriesSummary() {
            const reportFilters = this.analytics?.filters || {};
            return `Grouped by ${reportFilters.groupBy || this.filters.groupBy} using ${reportFilters.range || this.filters.range
                } as the range basis.`;
        },
    },
    watch: {
        "$route.query": {
            immediate: true,
            handler() {
                this.filters = this.buildFiltersFromRoute();
                this.loadAnalytics();
            },
        },
    },
    methods: {
        buildFiltersFromRoute() {
            return {
                groupBy: this.$route.query.groupBy || DEFAULT_FILTERS.groupBy,
                range: this.$route.query.range || DEFAULT_FILTERS.range,
                country: this.$route.query.country || DEFAULT_FILTERS.country,
                state: this.$route.query.state || DEFAULT_FILTERS.state,
                city: this.$route.query.city || DEFAULT_FILTERS.city,
                from: this.$route.query.from || DEFAULT_FILTERS.from,
                to: this.$route.query.to || DEFAULT_FILTERS.to,
            };
        },
        async loadAnalytics() {
            this.loadingMessage = "Loading analytics...";
            this.errorMessage = "";
            try {
                this.analytics = await getCodeAnalyticsFromApi(this.codeid, this.filters);
                this.loadingMessage = "";
            } catch (err) {
                this.analytics = null;
                this.errorMessage = err?.message || "Unable to load analytics.";
                this.loadingMessage = "";
            }
        },
        applyFilters() {
            this.$router.push({
                name: "code analytics",
                params: { codeid: this.codeid },
                query: {
                    groupBy: this.filters.groupBy,
                    range: this.filters.range,
                    country: this.filters.country,
                    state: this.filters.state,
                    city: this.filters.city,
                    from: this.filters.from,
                    to: this.filters.to,
                },
            });
        },
        resetFilters() {
            this.filters = { ...DEFAULT_FILTERS };
            this.applyFilters();
        },
        formatDateTime(value) {
            return Intl.DateTimeFormat("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
                second: "numeric",
                timeZoneName: "short",
            }).format(new Date(value));
        },
    },
};
</script>

<style scoped>
.analyticsView {
    width: 100%;
    display: grid;
    gap: 20px;
}

.analyticsHero,
.filtersPanel,
.panel,
.summaryCard {
    background: #f7f8fa;
    border: 1px solid #c8d0d8;
    border-radius: 18px;
    box-shadow: 0 10px 28px rgba(32, 54, 76, 0.08);
}

.analyticsHero {
    display: flex;
    justify-content: space-between;
    gap: 20px;
    align-items: flex-start;
    padding: 24px;
    text-align: left;
}

.eyebrow {
    margin: 0 0 6px;
    font-size: 0.85rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #56718b;
}

.analyticsHero h1,
.panel h2,
.panel h3 {
    margin-top: 0;
}

.heroMeta,
.panelHeading p,
.emptyState {
    color: #5b6d80;
}

.heroError {
    margin: 10px 0 0;
    color: #a12626;
}

.heroActions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.filtersPanel,
.panel {
    padding: 20px;
}

.filtersGrid,
.summaryGrid,
.tableGrid {
    display: grid;
    gap: 16px;
}

.filtersGrid {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
}

.field {
    text-align: left;
}

.field label {
    display: block;
    margin-bottom: 6px;
    font-size: 0.85rem;
    color: #56718b;
}

.filterActions {
    display: flex;
    gap: 10px;
    margin-top: 16px;
}

.summaryGrid {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.summaryCard {
    padding: 18px;
    display: grid;
    gap: 8px;
    text-align: left;
}

.summaryCard span {
    color: #56718b;
    font-size: 0.82rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
}

.summaryCard strong {
    font-size: 2rem;
    line-height: 1;
}

.chartShell {
    border: 1px solid #d4dbe3;
    border-radius: 16px;
    background: linear-gradient(180deg, #fbfcfe 0%, #f1f5f9 100%);
    padding: 12px;
}

.chartShell svg {
    width: 100%;
    height: 320px;
    display: block;
}

.chartGridLine {
    stroke: #d6dfeb;
    stroke-dasharray: 4 5;
}

.chartGridLabel,
.chartXAxisLabel {
    fill: #607086;
    font-size: 12px;
}

.chartXAxisLabel {
    text-anchor: middle;
}

.chartAxis {
    stroke: #b9c7d8;
}

.chartPath {
    fill: none;
    stroke: #0a7ea4;
    stroke-width: 3;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.chartDot {
    fill: #0a7ea4;
}

.tableGrid {
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
}

.panelWide {
    grid-column: 1 / -1;
}

table {
    width: 100%;
    border-collapse: collapse;
}

th,
td {
    text-align: left;
    padding: 10px 8px;
    border-bottom: 1px solid #d4dbe3;
    vertical-align: top;
}

th {
    font-size: 0.8rem;
    color: #56718b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

@media (max-width: 900px) {
    .analyticsHero {
        flex-direction: column;
    }

    .heroActions,
    .filterActions {
        width: 100%;
    }
}
</style>
